/**
 * The visual rotation of a racer (PlayerNetState.rot): how the character is turned on
 * screen, which the physics does not track.
 *
 * Convention: radians, clockwise on screen positive (the canvas convention with y pointing
 * down), measured before the gravity mirror: renderers rotate first and then mirror the
 * character vertically when gravity is reversed.
 *
 * - cube: spins forward while airborne (half a turn over a normal jump), snaps to the
 *   nearest quarter turn after landing, like in Geometry Dash;
 * - ship and wave: the nose follows the direction of flight;
 * - ball: rolls along its surface;
 * - UFO: tilts a little with its vertical speed;
 * - robot: upright on the ground, leaning back while its thrust lifts it (ROBOT_THRUST_LEAN: the
 *   renderer lights its jets then, see robotPose in render/racer.ts) and a little with its
 *   vertical speed in the air;
 * - spider: upright.
 */
import { DT, type Gravity, type Mode, SPEEDS, type SpeedKey } from '@cube-rush/shared';

/** Cube spin while airborne: half a turn during a normal jump (~0.45 s). */
export const CUBE_SPIN_SPEED = Math.PI / 0.45;
/** Radius the ball rolls on, in blocks. */
export const BALL_RADIUS = 0.5;
/** Ship nose angle limit, radians. */
const SHIP_MAX_TILT = 0.9;
const UFO_TILT_PER_SPEED = 0.025;
const UFO_MAX_TILT = 0.3;
/**
 * Robot: its lean while thrusting, and in the air (back rising, forward falling) per block/s of
 * vertical speed, within limits — never upright in the air: the renderer tucks its legs by it.
 */
export const ROBOT_THRUST_LEAN = -0.22;
const ROBOT_TILT_PER_SPEED = 0.012;
const ROBOT_MIN_TILT = 0.06;
const ROBOT_MAX_TILT = 0.12;
/** Per-second rates of the exponential approach to a target angle. */
const CUBE_SNAP_RATE = 32;
const SHIP_FOLLOW_RATE = 18;
const WAVE_FOLLOW_RATE = 60;
const UFO_FOLLOW_RATE = 10;
const ROBOT_FOLLOW_RATE = 16;

const QUARTER = Math.PI / 2;
const TURN = Math.PI * 2;

/** What the pose needs to know about a racer's physical state. */
export interface PoseState {
  mode: Mode;
  gravity: Gravity;
  speed: SpeedKey;
  vy: number;
  grounded: boolean;
  alive: boolean;
  /** Robot: ticks of jump thrust left (see PlayerState.thrust). */
  thrust: number;
}

/** Wraps an angle into (-π, π]. */
export function wrapAngle(angle: number): number {
  const wrapped = angle - TURN * Math.floor((angle + Math.PI) / TURN);
  return wrapped === -Math.PI ? Math.PI : wrapped;
}

/** Signed shortest turn from `from` to `to`, in (-π, π]. */
export function angleDelta(from: number, to: number): number {
  return wrapAngle(to - from);
}

/** Interpolates angles along the shortest arc. */
export function lerpAngle(from: number, to: number, t: number): number {
  return from + angleDelta(from, to) * t;
}

/** Nose direction of a racer flying with vertical speed `vy` at horizontal speed `vx`. */
export function flightAngle(vy: number, vx: number, limit: number): number {
  const angle = -Math.atan2(vy, vx);
  return Math.min(limit, Math.max(-limit, angle));
}

/** Rotation of a ball that has rolled to `x` (used for ghosts, whose samples are sparse). */
export function ballRollAngle(x: number, gravity: Gravity): number {
  return wrapAngle((x / BALL_RADIUS) * gravity);
}

/**
 * Tracks one racer's rotation tick by tick, so that it can be interpolated between ticks
 * exactly like the position.
 */
export class PoseTracker {
  private angle = 0;
  private previous = 0;
  private mode: Mode | null = null;

  /** Rotation after the last step. */
  get current(): number {
    return this.angle;
  }

  /** Rotation for rendering between the previous step (alpha 0) and the last one (alpha 1). */
  at(alpha: number): number {
    return this.previous + (this.angle - this.previous) * alpha;
  }

  /** Starts over (new attempt or level): no interpolation from the old pose. */
  reset(angle = 0): void {
    this.angle = angle;
    this.previous = angle;
    this.mode = null;
  }

  /** Advances by `dt` game seconds (one simulation tick by default). */
  step(s: PoseState, dt: number = DT): void {
    this.previous = this.angle;
    if (!s.alive) return;
    if (s.mode !== this.mode) {
      // A cube turned by quarter turns looks the same: the robot and the spider stand on their
      // feet from the portal on, without a spin back.
      if (s.mode === 'robot' || s.mode === 'spider') {
        const turns = Math.round(this.angle / QUARTER) * QUARTER;
        this.angle -= turns;
        this.previous -= turns;
      }
      this.mode = s.mode;
    }
    const vx = SPEEDS[s.speed];
    switch (s.mode) {
      case 'cube':
        if (s.grounded) {
          const target = Math.round(this.angle / QUARTER) * QUARTER;
          this.approach(target, CUBE_SNAP_RATE, dt);
        } else {
          this.angle += CUBE_SPIN_SPEED * dt * s.gravity;
        }
        break;
      case 'ball':
        this.angle += (vx / BALL_RADIUS) * dt * s.gravity;
        break;
      case 'ship':
        this.approach(this.near(flightAngle(s.vy, vx, SHIP_MAX_TILT)), SHIP_FOLLOW_RATE, dt);
        break;
      case 'wave':
        this.approach(this.near(flightAngle(s.vy, vx, QUARTER)), WAVE_FOLLOW_RATE, dt);
        break;
      case 'ufo': {
        const tilt = Math.min(UFO_MAX_TILT, Math.max(-UFO_MAX_TILT, -s.vy * UFO_TILT_PER_SPEED));
        this.approach(this.near(tilt), UFO_FOLLOW_RATE, dt);
        break;
      }
      case 'robot': {
        // Rising, the robot leans back (nose up); its speed counts from its feet.
        const rise = s.vy * s.gravity;
        const tilt = Math.min(
          ROBOT_MAX_TILT,
          Math.max(ROBOT_MIN_TILT, Math.abs(rise) * ROBOT_TILT_PER_SPEED),
        );
        const lean = s.thrust > 0 ? ROBOT_THRUST_LEAN : s.grounded ? 0 : rise > 0 ? -tilt : tilt;
        this.approach(this.near(lean), ROBOT_FOLLOW_RATE, dt);
        break;
      }
      case 'spider':
        this.approach(this.near(0), CUBE_SNAP_RATE, dt);
        break;
    }
    // Keep the numbers small without breaking the interpolation from `previous`.
    if (this.angle > Math.PI || this.angle <= -Math.PI) {
      const shift = this.angle - wrapAngle(this.angle);
      this.angle -= shift;
      this.previous -= shift;
    }
  }

  /** `target` moved by whole turns to the copy nearest the current angle. */
  private near(target: number): number {
    return this.angle + angleDelta(this.angle, target);
  }

  private approach(target: number, rate: number, dt: number): void {
    this.angle += (target - this.angle) * (1 - Math.exp(-rate * dt));
  }
}
